import type { GenericError, RequestStatus } from '@skynet/data-ws-core';

/**
 * Shared KPI commands common to every domain (loans, retainers, …). Backend
 * contract — string values must stay verbatim. Per-domain command enums (e.g.
 * `LoanKPI`, `RetainerKPI`) are combined with these by each app instance.
 */
export enum KpiCommands {
  AddKPI = 'AddKPI',
  EditKPI = 'EditKPI',
  RemoveKPI = 'RemoveKPI',
}

/**
 * Shape of the command map a domain instance passes to {@link initKpiSlice}.
 * It combines the shared {@link KpiCommands} with the per-domain listen command
 * (`KPI`), e.g. `{ ...KpiCommands, ...LoanKpiCommands }`.
 */
export interface KpiCommandMap {
  AddKPI: string;
  EditKPI: string;
  RemoveKPI: string;
  KPI: string;
}

export interface JsonKpi {
  ID?: number;
  Spread: number;
  Liquidity: number;
  Ticker: string;
  ExchangeID: number;
  Level: number;
}

export interface JsonKpiList {
  Description: string;
  All: JsonKpi[];
}

export const KpiTarget = {
  Spread: 'Spread',
  Depth: 'Depth',
} as const;

export interface FormKpi {
  id?: number;
  target: keyof typeof KpiTarget;
  spread: string;
  liquidity: string;
  ticker: string;
  exchangeID: number;
  level: number;
}

interface PayloadKpiCommon {
  loanID?: number;
  ticker: string;
}

interface PayloadKpiSpread extends PayloadKpiCommon {
  liquidity: string;
  level: number;
  exchangeID: number;
}

interface PayloadKpiDepth extends PayloadKpiCommon {
  spread: string;
}

export type PayloadKpi = PayloadKpiSpread | PayloadKpiDepth;

export interface KpiState {
  error: null | GenericError;
  requestStatus: RequestStatus;
  formKpi: FormKpi[] | null;
  kpiList: JsonKpi[] | null;
  selectedEditKpi: JsonKpi | null;
  resAddKpi: RequestStatus;
  resEditKpi: RequestStatus;
  resRemoveKpi: RequestStatus;
}

/**
 * Payload of a domain KPI listen response (e.g. `LoanKPI`, `RetainerKPI`).
 * The `command` is the per-domain listen command string; only `kpi` is
 * consumed generically by {@link initKpiSlice}.
 */
export interface JsonKpiSuccess {
  command: string;
  kpi: JsonKpi[];
}
